import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Calendar, CreditCard, ArrowRight, Loader2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSafePostHog } from '../components/PostHogProvider';

const Subscription = () => {
  const posthog = useSafePostHog();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => { loadSubscription(); }, []);

  useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      toast.success('Payment successful! Your subscription is being activated.');
      const timer = setTimeout(() => loadSubscription(), 2000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const loadSubscription = async () => {
    try { const r = await api.get('/subscription'); setSubscription(r.data); }
    catch { toast.error('Failed to load subscription'); }
    finally { setLoading(false); }
  };

  const handleCheckout = async (billingPeriod) => {
    posthog.capture('subscription_clicked', {
      plan_name: billingPeriod === 'yearly' ? 'premium_yearly' : 'premium_monthly',
      price: billingPeriod === 'yearly' ? '$79.99' : '$8.99',
      source: 'subscription_page',
    });
    setCheckoutLoading(billingPeriod);
    try {
      const r = await api.post('/checkout/create', { billing_period: billingPeriod });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment error. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return <DashboardLayout title="SUBSCRIPTION"><div className="flex items-center justify-center py-32"><div className="w-16 h-16 border-4 border-[#FF6B6B] border-t-transparent rounded-full animate-spin"></div></div></DashboardLayout>;
  }

  const isPremium = subscription?.plan === 'premium';

  const formatDate = (isoDate) => {
    if (!isoDate) return null;
    return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <DashboardLayout title="SUBSCRIPTION">
      <div className={`mb-10 rounded-2xl p-6 border-2 ${isPremium ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white border-[#1B4332]/8'}`} data-testid="current-plan">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className={`text-[10px] uppercase tracking-[0.15em] font-bold mb-1 ${isPremium ? 'text-[#BFDB38]' : 'text-[#FF6B6B]'}`}>CURRENT PLAN</p>
            <p className={`text-4xl font-black capitalize ${isPremium ? 'text-white' : 'text-[#1B4332]'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>{subscription?.plan}</p>
          </div>
          {isPremium && (
            <div className="bg-[#BFDB38] text-[#1B4332] px-5 py-2.5 rounded-full font-black text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" strokeWidth={2.5} /> PREMIUM MEMBER
            </div>
          )}
        </div>

        {isPremium && subscription?.renewal_date && (
          <div className="mt-5 flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2.5">
              <Calendar className="w-4 h-4 text-[#BFDB38]" strokeWidth={2.5} />
              <span className="text-sm text-white/80">
                <span className="font-bold">Renews:</span> {formatDate(subscription.renewal_date)}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2.5">
              <CreditCard className="w-4 h-4 text-[#BFDB38]" strokeWidth={2.5} />
              <span className="text-sm text-white/80">
                <span className="font-bold">Billing:</span> {subscription.billing_period === 'yearly' ? 'Annual ($79.99/yr)' : 'Monthly ($8.99/mo)'}
              </span>
            </div>
          </div>
        )}

        {!isPremium && (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-[#1B4332]/3 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50 mb-1">INGREDIENT SCANS</p>
              <p className="text-2xl font-black text-[#1B4332]">{subscription?.scan_count} / {subscription?.scan_limit}</p>
            </div>
            <div className="bg-[#1B4332]/3 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50 mb-1">BEHAVIOR CHATS</p>
              <p className="text-2xl font-black text-[#1B4332]">{subscription?.chat_count} / {subscription?.chat_limit}</p>
            </div>
          </div>
        )}
      </div>

      {!isPremium && (
        <>
          <h2 className="text-3xl font-black text-[#1B4332] mb-6 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>UPGRADE TO PREMIUM</h2>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto mb-8">
            <div className="bg-[#1B4332] text-white rounded-3xl p-5 sm:p-8 relative ring-4 ring-[#BFDB38]/30 shadow-xl shadow-[#1B4332]/20 z-10 scale-100 sm:scale-[1.02]" data-testid="monthly-plan">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#BFDB38] text-[#1B4332] px-6 py-2 rounded-full text-xs font-black uppercase whitespace-nowrap shadow-lg">
                MOST POPULAR
              </div>
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/50 mb-2">MONTHLY</p>
              <div className="mb-6">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg text-white/30 line-through font-bold">$12.99</span>
                  <p className="text-5xl font-black text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>$8.99<span className="text-base font-normal text-white/50">/month</span></p>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                {['Complete Peace of Mind (Unlimited Scans)', 'Unlimited behavior chat', 'Early Access to AI Health Predictor', 'Detailed Health Trend Reports'].map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-white text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleCheckout('monthly')}
                disabled={checkoutLoading !== null}
                className="w-full rounded-full font-bold bg-[#BFDB38] text-[#1B4332] hover:bg-[#a8c232] h-12 text-base hover:-translate-y-0.5 transition-all shadow-md"
                data-testid="checkout-monthly-btn"
              >
                {checkoutLoading === 'monthly' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Subscribe Monthly <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} /></>
                )}
              </Button>
            </div>

            <div className="bg-white border-2 border-[#1B4332]/10 rounded-3xl p-5 sm:p-8 relative shadow-sm" data-testid="yearly-plan">
              <div className="flex items-center justify-between mb-2">
                 <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/50">YEARLY</p>
                 <span className="text-[10px] font-black text-[#BFDB38] uppercase tracking-wider">BEST VALUE - SAVE 25%</span>
              </div>
              <div className="mb-4">
                <div className="flex items-baseline gap-2 mb-1">
                   <span className="text-lg text-gray-400 line-through font-bold">$119.99</span>
                   <p className="text-5xl font-black text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>$79.99<span className="text-base font-normal text-[#1B4332]/50">/year</span></p>
                </div>
                <p className="text-sm text-[#BFDB38] font-bold mt-1">Limited Slots for Early Supporters</p>
              </div>
              <ul className="space-y-3 mb-8 mt-6">
                {['Complete Peace of Mind (Unlimited Scans)', 'Unlimited behavior chat', 'Early Access to AI Health Predictor', 'Detailed Health Trend Reports', 'Priority access to new AI Models'].map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-[#1B4332]/80 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleCheckout('yearly')}
                disabled={checkoutLoading !== null}
                className="w-full rounded-full font-bold bg-[#1B4332] text-white hover:bg-[#1B4332]/90 h-12 text-base hover:-translate-y-0.5 transition-all"
                data-testid="checkout-yearly-btn"
              >
                {checkoutLoading === 'yearly' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Secure Early Bird Discount <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} /></>
                )}
              </Button>
            </div>
          </div>

          <div className="max-w-4xl mx-auto text-center space-y-2">
            <p className="text-xs text-[#1B4332]/40 uppercase tracking-wider font-bold">
              Secure payment powered by Dodo Payments
            </p>
            <p className="text-xs text-[#1B4332]/30">
              Cancel anytime. Your subscription will remain active until the end of the billing period.
            </p>
          </div>
        </>
      )}

      {isPremium && (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black text-[#1B4332] mb-6 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>YOUR PREMIUM BENEFITS</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Unlimited Scans', desc: 'Scan all the cat food you want' },
              { label: 'Unlimited Chat', desc: 'Ask our AI anything about your cat' },
              { label: 'Advanced Analytics', desc: 'Deep insights into diet health' },
              { label: 'Priority Support', desc: 'Get help when you need it' }
            ].map((b, i) => (
              <div key={i} className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-5 text-center" data-testid={`benefit-${i}`}>
                <div className="w-10 h-10 bg-[#BFDB38]/15 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Check className="w-5 h-5 text-[#1B4332]" strokeWidth={3} />
                </div>
                <p className="font-black text-[#1B4332] text-sm mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>{b.label}</p>
                <p className="text-xs text-[#1B4332]/50">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Subscription;
